"use client"

import type React from "react"
import { useEffect, useState } from "react"
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Grid,
  Divider,
  Alert,
  Chip,
  LinearProgress,
  Card,
  CardContent,
  CardHeader,
} from "@mui/material"
import { CloudUpload, Person, Work, Description, CheckCircle, Email, Phone, LocationOn } from "@mui/icons-material"
import axios from "axios"
import { baseURL, config, requestID } from "../../../common/utils"
import { useLocation } from "react-router"
import { IonContent, IonPage } from "@ionic/react"
import { fetchJobById } from "../../../api/jobs/jobs"

const JobApplicationForm: React.FC = () => {
  const location = useLocation()
  // Extract job ID from URL path
  const pathSegments = location.pathname.split("/")
  const jobId = pathSegments[pathSegments.length - 1]

  const [job, setJob] = useState<any>(null)

  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>("")
  const [success, setSuccess] = useState<boolean>(false)
  const [formData, setFormData] = useState({
    job_id: jobId,
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    phone: "",
    age: "",
    yearsOfExperience: "",
    location: "",
    resume: null,
    message: "",
  })
  const loadJob = async () => {

    const data = {
      job_id: jobId
    }
    try {
      setLoading(true)
      setError(null)
      const res = await fetchJobById(data)
      console.log("Job detailssss:", res.data)
      setJob(res.data)
    } catch (err) {
      console.error("Error loading job:", err)
      setError("Failed to load job details")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (jobId) {
      loadJob()
    }
  }, [jobId])
  
  const handleChange = (e) => {
    const { name, value, files } = e.target
    if (name === "resume" && files) {
      const file = files[0]
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ]
      const maxSize = 2 * 1024 * 1024

      if (!allowedTypes.includes(file.type)) {
        setError("Please upload a PDF or Word document.")
        return
      }

      if (file.size > maxSize) {
        setError("File size must be less than 2MB.")
        return
      }

      setError("")
      setFormData((prev) => ({
        ...prev,
        [name]: file,
      }))
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError("")

    try {
      const payload = new FormData()
      payload.append("requestID", requestID())
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          payload.append(key, value)
        }
      })

      const res = await axios.post(`${baseURL}/jobs/submitApplication`, payload, config)
      console.log("submit" ,res)
      setSuccess(true)
    } catch (err) {
      console.error(err)
      setError("Failed to submit application. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <IonPage>
        <IonContent>
          <Container maxWidth="sm" sx={{ mt: 8 }}>
            <Card elevation={0} sx={{ textAlign: "center", py: 6 }}>
              <CardContent>
                <CheckCircle sx={{ fontSize: 80, color: "success.main", mb: 2 }} />
                <Typography variant="h4" gutterBottom fontWeight="bold">
                  Application Submitted!
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Thank you for your interest. We'll review your application and get back to you soon.
                </Typography>
                {/* <Button variant="outlined" onClick={() => setSuccess(false)} size="large">
                  Submit Another Application
                </Button> */}
              </CardContent>
            </Card>
          </Container>
        </IonContent>
      </IonPage>
    )
  }

  return (
    <IonPage>
      <IonContent>
        <Box
          sx={{
            minHeight: "100vh",
            // background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            py: 4,
          }}
        >
          {/* <Container maxWidth="md"> */}
            <Paper
              elevation={24}
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(10px)",
              }}
            >
              {loading && <LinearProgress />}

              <CardHeader
                sx={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                  textAlign: "center",
                  py: 4,
                }}
                title={
                  <Typography variant="h3" fontWeight="bold">
                    {job ? job.title : "Loading..."} - Job Application
                  </Typography>
                }
                subheader={
                  <Chip
                    label={`Job ID: ${jobId}`}
                    sx={{
                      mt: 1,
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      color: "white",
                      fontWeight: "bold",
                    }}
                  />
                }
              />

              <CardContent sx={{ p: 4 }}>
                <Box component="form" onSubmit={handleSubmit} noValidate>
                  {/* Personal Information Section */}
                  <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                      <Person sx={{ mr: 1, color: "primary.main" }} />
                      <Typography variant="h5" fontWeight="bold">
                        Personal Information
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 3 }} />

                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="First Name"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          required
                          variant="outlined"
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Last Name"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          required
                          variant="outlined"
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Middle Name"
                          name="middleName"
                          value={formData.middleName}
                          onChange={handleChange}
                          variant="outlined"
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Email Address"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          variant="outlined"
                          InputProps={{
                            startAdornment: <Email sx={{ mr: 1, color: "action.active" }} />,
                          }}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Phone Number"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          variant="outlined"
                          InputProps={{
                            startAdornment: <Phone sx={{ mr: 1, color: "action.active" }} />,
                          }}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Professional Information Section */}
                  <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                      <Work sx={{ mr: 1, color: "primary.main" }} />
                      <Typography variant="h5" fontWeight="bold">
                        Professional Information
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 3 }} />

                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Age"
                          name="age"
                          type="number"
                          value={formData.age}
                          onChange={handleChange}
                          required
                          variant="outlined"
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Years of Experience"
                          name="yearsOfExperience"
                          type="number"
                          value={formData.yearsOfExperience}
                          onChange={handleChange}
                          required
                          variant="outlined"
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth variant="outlined">
                          <InputLabel>Location</InputLabel>
                          <Select
                            name="location"
                            value={formData.location}
                            onChange={handleChange}
                            label="Location"
                            required
                            startAdornment={<LocationOn sx={{ mr: 1, color: "action.active" }} />}
                            sx={{ borderRadius: 2 }}
                          >
                            <MenuItem value="New York">New York</MenuItem>
                            <MenuItem value="Los Angeles">Los Angeles</MenuItem>
                            <MenuItem value="San Francisco">San Francisco</MenuItem>
                            <MenuItem value="Chicago">Chicago</MenuItem>
                            <MenuItem value="Remote">Remote</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Resume & Cover Letter Section */}
                  <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                      <Description sx={{ mr: 1, color: "primary.main" }} />
                      <Typography variant="h5" fontWeight="bold">
                        Resume & Cover Letter
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 3 }} />

                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            border: "2px dashed",
                            borderColor: formData.resume ? "success.main" : "grey.300",
                            borderRadius: 2,
                            p: 3,
                            textAlign: "center",
                            backgroundColor: formData.resume ? "success.50" : "grey.50",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              borderColor: "primary.main",
                              backgroundColor: "primary.50",
                            },
                          }}
                        >
                          <input
                            type="file"
                            name="resume"
                            hidden
                            onChange={handleChange}
                            accept=".pdf,.doc,.docx"
                            id="resume-upload"
                          />
                          <label htmlFor="resume-upload" style={{ cursor: "pointer" }}>
                            <CloudUpload
                              sx={{
                                fontSize: 48,
                                color: formData.resume ? "success.main" : "grey.400",
                                mb: 1,
                              }}
                            />
                            <Typography variant="h6" gutterBottom>
                              {formData.resume ? "Resume Uploaded" : "Upload Resume"}
                            </Typography>
                            {formData.resume ? (
                              <Chip label={formData.resume.name} color="success" sx={{ mt: 1 }} />
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Click to upload PDF, DOC, or DOCX (max 2MB)
                              </Typography>
                            )}
                          </label>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Cover Letter / Message"
                          name="message"
                          multiline
                          rows={5}
                          value={formData.message}
                          onChange={handleChange}
                          variant="outlined"
                          placeholder="Tell us why you're interested in this position..."
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Error Alert */}
                  {error && (
                    <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                      {error}
                    </Alert>
                  )}

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={loading}
                    sx={{
                      py: 2,
                      fontSize: "1.1rem",
                      fontWeight: "bold",
                      borderRadius: 2,
                      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      "&:hover": {
                        background: "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                      },
                    }}
                  >
                    {loading ? "Submitting Application..." : "Submit Application"}
                  </Button>
                </Box>
              </CardContent>
            </Paper>
          {/* </Container> */}
        </Box>
      </IonContent>
    </IonPage>
  )
}

export default JobApplicationForm
